import { z } from 'zod';
import { emailDangNhapSchema, matKhauMoiSchema } from './core.js';
import { MODULE_LABEL, PERMISSION_LABEL, coLocKenh, moduleIdSchema, permissionLevelSchema, phamViDuLieuSchema, roleKeySchema, userSummarySchema, } from './core.js';
/* ------------------------------------------------------------------ */
/* Hằng số                                                             */
/* ------------------------------------------------------------------ */
export const TWO_FACTOR_METHODS = ['security_key', 'authenticator', 'sms', 'none'];
export const twoFactorMethodSchema = z.enum(TWO_FACTOR_METHODS);
export const TWO_FACTOR_LABEL = {
    security_key: 'Khoá bảo mật',
    authenticator: 'Ứng dụng xác thực',
    sms: 'SMS',
    none: 'Chưa bật',
};
export const TWO_FACTOR_TONE = {
    security_key: 'ok',
    authenticator: 'ok',
    sms: 'warn',
    none: 'bad',
};
/** Quá số ngày này chưa rà soát bảo mật thì cảnh báo. */
export const HAN_RA_SOAT_NGAY = 90;
/* ------------------------------------------------------------------ */
/* Thành viên                                                          */
/* ------------------------------------------------------------------ */
export const teamMemberSchema = userSummarySchema.extend({
    /// Các kênh được giao, lấy từ ChannelAssignment của module channels.
    channels: z.array(z.object({ id: z.string(), name: z.string(), role: z.string() })),
    /**
     * Hạn của lời mời **còn hiệu lực** gần nhất, `null` nếu không có cái nào.
     *
     * Giao diện cần để hiện "Link mời hết hạn <ngày>" trên thẻ người ở trạng thái
     * `invited`. Token thì không trả ra: trong database chỉ có bản băm SHA-256,
     * link gốc không dựng lại được — muốn có link phải tạo lại cái mới.
     */
    inviteExpiresAt: z.iso.datetime().nullable(),
    /**
     * Lúc tài khoản bị khoá, `null` khi đang mở. Chỉ có nghĩa khi
     * `status = 'disabled'`.
     */
    lockedAt: z.iso.datetime().nullable(),
    /**
     * Tên người đã bấm khoá, `null` nếu không rõ (người đó đã bị xoá, hoặc tài
     * khoản bị khoá từ trước khi có hai cột này). Nguồn đầy đủ "ai khoá ai lúc
     * nào" vẫn là nhật ký kiểm toán; đây chỉ để hiện trên thẻ thành viên.
     */
    lockedByName: z.string().nullable(),
    /** Lúc lưu trữ, `null` khi tài khoản chưa lưu trữ. */
    archivedAt: z.iso.datetime().nullable(),
    /** Tên người đã bấm lưu trữ, `null` nếu không rõ. */
    archivedByName: z.string().nullable(),
    /** Lý do quản trị nhập lúc lưu trữ, ví dụ "Nghỉ việc 31/10/2026". */
    archiveReason: z.string().nullable(),
});
export const inviteMemberInputSchema = z.object({
    email: z.email(),
    name: z.string().trim().min(1).max(120),
    roleKey: roleKeySchema,
});
/**
 * Chuyện gì đã xảy ra với email đi kèm một lời mời / link đặt mật khẩu.
 *
 * - `da-gui`: SMTP nhận thư. Người nhận vẫn có thể không thấy (spam), nhưng
 *   phía mình đã xong.
 * - `chua-cau-hinh`: chưa khai SMTP trên máy chủ. **Không được** nói "đã gửi
 *   email" trong trường hợp này — giao diện phải mời quản trị sao chép link.
 * - `loi`: SMTP từ chối hoặc không nối được. `loi` có câu giải thích cho quản
 *   trị (sai mật khẩu ứng dụng, cổng bị chặn…).
 * - `khong-gui`: cố ý không gửi — quản trị chỉ bấm "Tạo lại link".
 */
export const trangThaiGuiMailSchema = z.enum(['da-gui', 'chua-cau-hinh', 'loi', 'khong-gui']);
export const ketQuaGuiMailSchema = z.object({
    trangThai: trangThaiGuiMailSchema,
    /// Câu giải thích cho quản trị khi `trangThai = 'loi'`, ngược lại `null`.
    loi: z.string().nullable(),
});
export const inviteResultSchema = z.object({
    inviteId: z.string(),
    email: z.string(),
    expiresAt: z.iso.datetime(),
    /**
     * Link mời. Luôn trả cho người có quyền `team:admin` — kể cả trên máy chủ
     * thật — để còn **phương án dự phòng sao chép link** khi email chưa cấu hình
     * hoặc gửi lỗi. Quản trị vốn tạo được link mới bất cứ lúc nào, nên trả link
     * không mở thêm quyền gì.
     *
     * `null` chỉ khi endpoint cố ý không dựng link.
     */
    inviteUrl: z.string().nullable(),
    /// Email đi kèm đã gửi được chưa. Xem `trangThaiGuiMailSchema`.
    mail: ketQuaGuiMailSchema,
});
/**
 * Đặt mật khẩu khi chấp nhận lời mời. Token nằm trên đường dẫn, không nằm trong
 * thân request để không lọt vào log thân JSON.
 */
export const acceptInviteInputSchema = z.object({
    // Cùng một luật độ dài với mọi chỗ đặt mật khẩu khác (module 00).
    password: matKhauMoiSchema,
});
export const acceptInviteResultSchema = z.object({
    email: z.email(),
    name: z.string(),
    /// Vai trò được gán sẵn lúc mời.
    roleKey: roleKeySchema,
});
export const updateMemberInputSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    /**
     * Khoá / mở khoá kèm trong cùng một lần lưu.
     *
     * Giao diện **không** dùng đường này nữa — nó gọi
     * `POST /users/:id/khoa` và `/mo-khoa` để có hộp xác nhận riêng. Giữ lại cho
     * tương thích, và server áp **cùng** bộ ràng buộc an toàn (không tự khoá
     * mình, không khoá quản trị viên hoạt động cuối cùng) cho cả hai đường.
     */
    status: z.enum(['active', 'disabled']).optional(),
    roleKeys: z.array(roleKeySchema).max(5).optional(),
    /// Email đăng nhập mới. Phải nhập hai lần như ở "Tài khoản của tôi".
    email: emailDangNhapSchema.optional(),
    confirmEmail: emailDangNhapSchema.optional(),
})
    .refine((d) => d.email === undefined || d.email === d.confirmEmail, {
    message: 'Hai lần nhập email không khớp',
    path: ['confirmEmail'],
});
export const assignChannelsInputSchema = z.object({
    channelIds: z.array(z.string().min(1)).max(100),
    role: z.enum(['owner', 'member']).default('member'),
});
/* ------------------------------------------------------------------ */
/* Vai trò và ma trận quyền                                            */
/* ------------------------------------------------------------------ */
export const roleWithPermissionsSchema = z.object({
    id: z.string(),
    key: roleKeySchema,
    name: z.string(),
    /** Phạm vi dữ liệu của vai trò — nguồn sự thật của việc lọc theo kênh. */
    phamViDuLieu: phamViDuLieuSchema,
    memberCount: z.number().int().nonnegative(),
    permissions: z.record(moduleIdSchema, permissionLevelSchema),
});
/**
 * Thông điệp của lỗi "đặt `assigned` cho module không lọc kênh". Giao diện khoá
 * sẵn ô đó, nên lỗi này chỉ gặp khi gọi API trực tiếp — nhưng nó là **chặn
 * trước** của mục V4: cấu hình sai không bao giờ vào được database.
 */
export function thongDiepAssignedKhongHopLe(module) {
    return `Module "${MODULE_LABEL[module]}" không lọc theo kênh được giao, nên không nhận mức "${PERMISSION_LABEL.assigned}". Chọn ${PERMISSION_LABEL.none} hoặc ${PERMISSION_LABEL.view}.`;
}
/**
 * Thông điệp cảnh báo khi một vai trò **không phải quản trị** được để phạm vi
 * "Toàn công ty". Dùng chung cho giao diện (dải đỏ) và cho CLI kiểm tra ma trận,
 * để hai nơi không nói hai kiểu.
 */
export function thongDiepPhamViToanCongTy(tenVaiTro) {
    return `Người giữ vai trò "${tenVaiTro}" sẽ thấy TOÀN BỘ dữ liệu công ty: mọi kênh, mọi đối tác, mọi số tổng — kể cả kênh không được giao cho họ.`;
}
export const updateRolePermissionsInputSchema = z.object({
    permissions: z.record(moduleIdSchema, permissionLevelSchema).superRefine((map, ctx) => {
        for (const [module, level] of Object.entries(map)) {
            if (level === 'assigned' && !coLocKenh(module)) {
                ctx.addIssue({
                    code: 'custom',
                    path: [module],
                    message: thongDiepAssignedKhongHopLe(module),
                });
            }
        }
    }),
    /**
     * Phạm vi dữ liệu của vai trò. **Tuỳ chọn**: bỏ trống nghĩa là "giữ nguyên",
     * nên chỗ gọi cũ (chỉ gửi `permissions`) không đổi hành vi.
     */
    phamViDuLieu: phamViDuLieuSchema.optional(),
});
/* ------------------------------------------------------------------ */
/* Sổ bảo mật tài khoản Google                                         */
/* ------------------------------------------------------------------ */
/**
 * ⚠️ Chỉ trạng thái bảo mật. Không có trường nào chứa mật khẩu, mã khôi phục
 * hay mã 2FA, và không bao giờ được thêm.
 */
export const googleAccountRecordSchema = z.object({
    id: z.string(),
    /// Email đã che: `abc••••@gmail.com`.
    label: z.string(),
    channelIds: z.array(z.string()),
    channelNames: z.array(z.string()),
    twoFactorMethod: twoFactorMethodSchema,
    recoveryEmailSet: z.boolean(),
    suspiciousLoginNote: z.string().nullable(),
    lastReviewedAt: z.iso.datetime().nullable(),
    /// `true` khi quá HAN_RA_SOAT_NGAY ngày chưa rà soát.
    needsReview: z.boolean(),
});
export const googleAccountRecordInputSchema = z.object({
    label: z
        .string()
        .trim()
        .min(5)
        .max(120)
        .refine((v) => v.includes('•'), 'Email phải được che, ví dụ abc••••@gmail.com'),
    channelIds: z.array(z.string().min(1)).max(100).default([]),
    twoFactorMethod: twoFactorMethodSchema.default('none'),
    recoveryEmailSet: z.boolean().default(false),
    suspiciousLoginNote: z.string().trim().max(1000).nullish(),
    lastReviewedAt: z.iso.datetime().nullish(),
});
/* ------------------------------------------------------------------ */
/* Khoá tài khoản và thu hồi lời mời                                   */
/* ------------------------------------------------------------------ */
/**
 * Kết quả thu hồi một lời mời chưa nhận.
 *
 * Người chưa kích hoạt thì **xoá hẳn** (tài khoản và mọi dòng lời mời của email
 * đó): họ chưa có dữ liệu nào trong hệ thống nên không có gì phải giữ, và email
 * được giải phóng để mời lại.
 */
export const revokeInviteResultSchema = z.object({
    userId: z.string(),
    email: z.string(),
    /// Số dòng lời mời đã xoá theo email đó.
    invitesDeleted: z.number().int().nonnegative(),
});
/* ------------------------------------------------------------------ */
/* Lưu trữ nhân sự đã nghỉ                                             */
/* ------------------------------------------------------------------ */
/**
 * "Hồ sơ khi nghỉ" — ảnh chụp **cố định** lúc lưu trữ, nằm trong
 * `users.archiveSnapshot`.
 *
 * Chụp **trước** khi gỡ phân công kênh, nên về sau vẫn trả lời được "lúc nghỉ
 * người này đang giữ những kênh nào" dù kênh đã sang tay người khác.
 *
 * Mọi trường `nullish` vì bản ghi cũ (lưu trữ ở phiên bản trước) có thể thiếu;
 * giao diện phải chịu được dữ liệu khuyết.
 */
export const archiveSnapshotSchema = z.object({
    /// Lúc chụp — cũng là lúc lưu trữ.
    archivedAt: z.iso.datetime(),
    /// Ngày tạo tài khoản, dùng làm "ngày tham gia".
    joinedAt: z.iso.datetime().nullish(),
    lastActiveAt: z.iso.datetime().nullish(),
    roles: z.array(roleKeySchema).default([]),
    /// Kênh đang phụ trách lúc nghỉ, kèm vai trò trong kênh.
    channels: z.array(z.object({ id: z.string(), name: z.string(), role: z.string() })).default([]),
    reason: z.string().nullish(),
    archivedByName: z.string().nullish(),
});
export const archiveMemberInputSchema = z.object({
    /**
     * Lý do, ô nhập tự do. Bắt buộc: nó là thứ duy nhất nói vì sao người này
     * không còn trong công ty, và quản trị đọc lại nó ở tab "Lưu trữ".
     */
    reason: z.string().trim().min(1, 'Nhập lý do lưu trữ').max(500),
});
/** Một người trong tab "Lưu trữ". */
export const archivedMemberSchema = z.object({
    id: z.string(),
    email: z.email(),
    name: z.string(),
    avatarColor: z.string(),
    archivedAt: z.iso.datetime().nullable(),
    archivedByName: z.string().nullable(),
    archiveReason: z.string().nullable(),
    /// Vai trò lúc nghỉ, lấy từ ảnh chụp (không phải vai trò hiện tại).
    roles: z.array(roleKeySchema),
    /// Số kênh đang phụ trách lúc nghỉ.
    channelCount: z.number().int().nonnegative(),
});
/** Trang hồ sơ chỉ xem của người đã lưu trữ. */
export const archivedProfileSchema = z.object({
    member: teamMemberSchema,
    /// `null` với bản ghi lưu trữ trước khi có ảnh chụp.
    snapshot: archiveSnapshotSchema.nullable(),
    joinedAt: z.iso.datetime(),
});
/**
 * Khôi phục: tài khoản về `active` **và** phát link đặt lại mật khẩu.
 *
 * Không tự gán lại kênh cũ — quản trị gán tay, vì kênh đã sang người khác trong
 * lúc người này nghỉ.
 */
export const restoreMemberResultSchema = z.object({
    member: teamMemberSchema,
    /// Link đặt lại mật khẩu đã phát (và trạng thái gửi email).
    datLaiMatKhau: inviteResultSchema,
});
/* ------------------------------------------------------------------ */
/* Xoá cứng tài khoản                                                  */
/* ------------------------------------------------------------------ */
/**
 * Đếm tham chiếu tới một tài khoản ở các bảng **ngoài** nhóm "của riêng họ"
 * (phiên, vai trò, phân công kênh, lời mời gửi tới email họ). Chỉ khi mọi số
 * này bằng 0 thì mới cho xoá cứng.
 */
/**
 * Khoá là id bảng; giao diện tra nhãn tiếng Việt trong `NHAN_THAM_CHIEU`.
 *
 * `auditLogs` **không** đếm mọi dòng nhật ký: các action trong
 * `ACTION_XAC_THUC_CUA_MINH` bị bỏ qua.
 */
export const THAM_CHIEU_NGUOI_DUNG = [
    'auditLogs',
    'productionItems',
    'productionChecklists',
    'complianceIssues',
    'complianceLogs',
    'partnerContracts',
    'partnerTickets',
    'payoutSettings',
    'oauthInvites',
    'teamInvitesSent',
    'brandAccounts',
    'usersLocked',
    'usersArchived',
    'tyLeTruongNhom',
    'tyLeDoiNguKenh',
    'khoanChiDaNhap',
    'nhomDaGanKenh',
    'doiSoatNhanSu',
];
export const NHAN_THAM_CHIEU = {
    auditLogs: 'thao tác nghiệp vụ trong nhật ký',
    productionItems: 'thẻ sản xuất được giao',
    productionChecklists: 'mục checklist đã tick',
    complianceIssues: 'vụ bản quyền được giao',
    complianceLogs: 'dòng nhật ký bản quyền',
    partnerContracts: 'hợp đồng đã xoá',
    partnerTickets: 'ticket với Network',
    payoutSettings: 'cài đặt thực nhận',
    oauthInvites: 'link mời kết nối YouTube',
    teamInvitesSent: 'lời mời đã gửi cho người khác',
    brandAccounts: 'tài khoản thương hiệu đã kiểm',
    usersLocked: 'tài khoản người khác đã khoá',
    usersArchived: 'tài khoản người khác đã lưu trữ',
    tyLeTruongNhom: 'dòng tỷ lệ trưởng nhóm',
    tyLeDoiNguKenh: 'dòng tỷ lệ quản lý/editor của kênh',
    khoanChiDaNhap: 'khoản chi đã nhập',
    nhomDaGanKenh: 'lần gán kênh vào nhóm',
    doiSoatNhanSu: 'kỳ đối soát và khoản điều chỉnh',
};
/**
 * Nhật ký **xác thực của chính tài khoản đó** — những dòng không nói gì về việc
 * người này đã làm trong hệ thống, chỉ nói họ đã đăng nhập / đổi mật khẩu của
 * chính mình. Cổng xoá cứng **bỏ qua** đúng các action này khi đếm
 * `audit_logs`: nếu không, một lượt đăng nhập thử của tài khoản thêm nhầm là đã
 * khoá đường xoá.
 *
 * ⛔ **Danh sách cho phép tường minh, không dùng tiền tố `core.*`.** Tiền tố sẽ
 * âm thầm bỏ qua cả action nghiệp vụ đặt trong module `core` sau này. Thêm một
 * việc xác thực mới (bật/tắt 2FA, làm mới phiên, đăng nhập thất bại… — hiện
 * **chưa** có action nào trong code) thì phải thêm tên vào đây bằng tay; mặc
 * định một action mới **chặn** xoá, hướng an toàn.
 *
 * Các dòng này **không bị xoá** khi xoá cứng: khoá ngoại `audit_logs.userId` là
 * `SetNull`, nên dòng còn nguyên và chỉ mất liên kết tới `users`. Dấu vết "tài
 * khoản nào" nằm ở `meta` của dòng `team.member.delete` (email + tên).
 */
export const ACTION_XAC_THUC_CUA_MINH = [
    /// Đăng nhập thành công (`core.dangNhap`).
    'core.login',
    /// Đăng xuất (`core.dangXuat`).
    'core.logout',
    /// Tự đổi mật khẩu ở trang "Tài khoản của tôi".
    'core.account.password',
    /// Tự đổi email đăng nhập ở trang "Tài khoản của tôi".
    'core.account.email',
    /// Quản trị đặt lại mật khẩu bằng CLI `tai-khoan:dat-mat-khau` — dòng ghi
    /// `userId` của **chủ tài khoản**, không phải người chạy lệnh.
    'core.account.password-cli',
    /// Tài khoản được tạo bằng CLI `tai-khoan:tao-admin`, cũng ghi `userId` của
    /// chính tài khoản đó.
    'core.account.tao-admin-cli',
    /// Chấp nhận lời mời và đặt mật khẩu đầu tiên. Hiện route ghi `userId = null`
    /// (người đó chưa đăng nhập được lúc bấm), nên thực tế không bao giờ dính vào
    /// phép đếm — liệt kê ở đây để khỏi phải nghĩ lại nếu sau này ghi `userId`.
    'team.invite.accept',
];
export const deleteEligibilitySchema = z.object({
    userId: z.string(),
    /// Xoá cứng được hay không.
    xoaDuoc: z.boolean(),
    /// Vì sao không xoá được, `null` khi xoá được. Giao diện hiện nguyên câu này.
    lyDo: z.string().nullable(),
    /// Số bản ghi theo từng bảng chặn. Chỉ có khoá nào > 0.
    thamChieu: z.record(z.enum(THAM_CHIEU_NGUOI_DUNG), z.number().int().nonnegative()),
    /// Tổng số bản ghi chặn.
    tong: z.number().int().nonnegative(),
    /// Những thứ sẽ bị xoá kèm (phiên, vai trò, phân công kênh, lời mời của họ).
    seXoaKem: z.object({
        sessions: z.number().int().nonnegative(),
        roles: z.number().int().nonnegative(),
        channelAssignments: z.number().int().nonnegative(),
        invites: z.number().int().nonnegative(),
    }),
});
export const deleteMemberResultSchema = z.object({
    userId: z.string(),
    email: z.string(),
    name: z.string(),
});
/* ------------------------------------------------------------------ */
/* Nhật ký thao tác của một người                                      */
/* ------------------------------------------------------------------ */
export const auditEntrySchema = z.object({
    id: z.string(),
    /// Ví dụ `team.member.archive`, `core.login`, `channels.create`.
    action: z.string(),
    /// Phần trước dấu chấm đầu tiên của `action`, dùng để lọc theo module.
    module: z.string(),
    entity: z.string(),
    entityId: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const auditQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    /// Lọc theo module: khớp `action` bắt đầu bằng `<module>.`.
    module: z.string().trim().max(40).optional(),
    /// Khoảng thời gian, dạng `YYYY-MM-DD` (giờ Việt Nam, bao gồm cả hai đầu).
    tu: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    den: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
export const auditPageSchema = z.object({
    items: z.array(auditEntrySchema),
    nextCursor: z.string().nullable(),
    /// Các module có mặt trong nhật ký của người này, để dựng ô lọc.
    modules: z.array(z.string()),
});
/** Tổng hợp cho màn hình Nhân sự. */
export const teamSummarySchema = z.object({
    /**
     * Mọi số dưới đây **không tính người đã lưu trữ** (nhân sự đã nghỉ), trừ
     * chính `archived`.
     */
    members: z.number().int().nonnegative(),
    activeMembers: z.number().int().nonnegative(),
    invited: z.number().int().nonnegative(),
    /// Số tài khoản đang bị khoá tạm (`status = 'disabled'`).
    locked: z.number().int().nonnegative(),
    /// Số tài khoản đã lưu trữ (`status = 'archived'`). Nằm ngoài mọi số còn lại.
    archived: z.number().int().nonnegative(),
    accountsNeedingReview: z.number().int().nonnegative(),
    /**
     * Số **thành viên** chưa bật 2FA. Tính cả người đang ở trạng thái "đã mời"
     * (họ cũng hiện "2FA chưa bật" trên thẻ), **không** tính người đã khoá.
     *
     * Trước đây trường này đếm *tài khoản Google quản lý kênh* chưa bật 2FA, nên ô
     * thống kê hiện 0 trong khi mọi thẻ thành viên đều báo "2FA chưa bật". Số của
     * sổ tài khoản Google giờ nằm ở `googleAccountsWithout2fa`.
     */
    without2fa: z.number().int().nonnegative(),
    /// Số bản ghi trong sổ bảo mật tài khoản Google có `twoFactorMethod = 'none'`.
    googleAccountsWithout2fa: z.number().int().nonnegative(),
});
//# sourceMappingURL=team.js.map